import { useCallback, useEffect, useRef } from 'react';

import { getPanelElement, getPanelGroupElement } from 'react-resizable-panels';

import { DynamicSizeResult, LayoutArea, PanelConfig, PanelResizeHandlers, SizeConstraint, SizeConstraints } from '../types';

/**
 * Dynamic panel sizing utilities for intelligent layout optimization
 * Uses react-resizable-panels native hooks and methods for getting/setting sizes
 */

export interface ViewportInfo {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

export interface ContentRequirements {
  /** Minimum pixels needed for content to be usable */
  minHeight?: number;
  minWidth?: number;
  /** Optimal pixels for best user experience */
  optimalHeight?: number;
  optimalWidth?: number;
  /** Priority level (1-10, higher = more important) */
  priority: number;
  /** Panel type affects sizing strategy */
  type: 'navigation' | 'content' | 'toolbar' | 'status' | 'auxiliary';
}

export interface PanelSizeCalculation {
  defaultSize: number; // Percentage
  minSize: number; // Percentage
  maxSize: number; // Percentage
  reason: string; // Why this size was chosen
}

/**
 * Content requirements database - simplified for 5 core areas
 */
export const CONTENT_REQUIREMENTS: Record<LayoutArea, ContentRequirements> = {
  [LayoutArea.HEADER]: {
    minHeight: 48,
    optimalHeight: 60,
    priority: 8,
    type: 'toolbar',
  },
  [LayoutArea.LEFT_SIDEBAR]: {
    minWidth: 200,
    optimalWidth: 280,
    priority: 7,
    type: 'navigation',
  },
  [LayoutArea.CONTENT]: {
    minWidth: 400,
    minHeight: 300,
    priority: 10, // Highest priority
    type: 'content',
  },
  [LayoutArea.RIGHT_SIDEBAR]: {
    minWidth: 200,
    optimalWidth: 320,
    priority: 6,
    type: 'auxiliary',
  },
  [LayoutArea.FOOTER]: {
    minHeight: 40,
    optimalHeight: 48,
    priority: 4,
    type: 'status',
  },
  // Additional layout areas
  [LayoutArea.BOTTOM_PANEL]: {
    minHeight: 100,
    optimalHeight: 250,
    priority: 3,
    type: 'content',
  },
  [LayoutArea.TOP_PANEL]: {
    minHeight: 60,
    optimalHeight: 120,
    priority: 3,
    type: 'content',
  },
  [LayoutArea.TABS]: {
    minHeight: 32,
    optimalHeight: 40,
    priority: 2,
    type: 'navigation',
  },
  [LayoutArea.SECONDARY_TABS]: {
    minHeight: 24,
    optimalHeight: 32,
    priority: 2,
    type: 'navigation',
  },
  [LayoutArea.ACTION_BUTTONS]: {
    minHeight: 40,
    optimalHeight: 60,
    priority: 2,
    type: 'toolbar',
  },
  [LayoutArea.BREADCRUMB]: {
    minHeight: 24,
    optimalHeight: 36,
    priority: 2,
    type: 'navigation',
  },
  [LayoutArea.SUBHEADER]: {
    minHeight: 32,
    optimalHeight: 48,
    priority: 2,
    type: 'toolbar',
  },
};

/**
 * Get current viewport information
 */
export function getViewportInfo(): ViewportInfo {
  const width = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const height = typeof window !== 'undefined' ? window.innerHeight : 800;

  return {
    width,
    height,
    isMobile: width < 768,
    isTablet: width >= 768 && width < 1024,
    isDesktop: width >= 1024,
  };
}

/**
 * Calculate optimal panel sizes based on content needs and available space
 */
export function calculateOptimalSizes(
  visibleAreas: LayoutArea[],
  direction: 'horizontal' | 'vertical' = 'vertical',
  viewport?: ViewportInfo,
): Record<LayoutArea, PanelSizeCalculation> {
  const viewportInfo = viewport || getViewportInfo();
  const results: Record<LayoutArea, PanelSizeCalculation> = {} as Record<LayoutArea, PanelSizeCalculation>;

  // Separate areas by direction relevance
  const relevantAreas = visibleAreas.filter((area) => {
    const req = CONTENT_REQUIREMENTS[area];
    if (direction === 'horizontal') {
      // For horizontal layouts, we care about width requirements
      return req.minWidth !== undefined || area === LayoutArea.CONTENT;
    } else {
      // For vertical layouts, we care about height requirements
      return req.minHeight !== undefined || area === LayoutArea.CONTENT;
    }
  });

  if (relevantAreas.length === 0) {
    return results;
  }

  // Calculate available space
  const availableSpace = direction === 'horizontal' ? viewportInfo.width : viewportInfo.height;

  // Calculate total required space and priorities
  let totalMinRequired = 0;
  let totalOptimalRequired = 0;
  let totalPriority = 0;

  const areaData = relevantAreas.map((area) => {
    const req = CONTENT_REQUIREMENTS[area];
    const minRequired =
      direction === 'horizontal' ? req.minWidth || (area === LayoutArea.CONTENT ? 400 : 0) : req.minHeight || (area === LayoutArea.CONTENT ? 200 : 0);
    const optimalRequired = direction === 'horizontal' ? req.optimalWidth || req.minWidth || minRequired : req.optimalHeight || req.minHeight || minRequired;

    totalMinRequired += minRequired;
    totalOptimalRequired += optimalRequired;
    totalPriority += req.priority;

    return {
      area,
      req,
      minRequired,
      optimalRequired,
    };
  });

  // Calculate size distribution strategy
  const spacingBuffer = 20; // Reserve some space for padding/margins
  const usableSpace = Math.max(availableSpace - spacingBuffer, totalMinRequired);

  // If we have enough space for optimal sizes, use them
  if (usableSpace >= totalOptimalRequired) {
    // Plenty of space - use optimal sizes with content getting extra
    const contentArea = areaData.find((d) => d.area === LayoutArea.CONTENT);
    const nonContentAreas = areaData.filter((d) => d.area !== LayoutArea.CONTENT);

    let usedSpace = 0;

    // Give non-content areas their optimal size
    nonContentAreas.forEach(({ area, optimalRequired }) => {
      const percentage = Math.round((optimalRequired / usableSpace) * 100);
      results[area] = {
        defaultSize: percentage,
        minSize: Math.round(((CONTENT_REQUIREMENTS[area].minHeight || CONTENT_REQUIREMENTS[area].minWidth || optimalRequired * 0.7) / usableSpace) * 100),
        maxSize: Math.min(percentage * 1.5, 25), // Don't let toolbars get too big
        reason: `Optimal size (${optimalRequired}px) with plenty of space available`,
      };
      usedSpace += optimalRequired;
    });

    // Give remaining space to content
    if (contentArea) {
      const remainingSpace = usableSpace - usedSpace;
      const percentage = Math.round((remainingSpace / usableSpace) * 100);
      results[contentArea.area] = {
        defaultSize: Math.max(percentage, 40), // Ensure content gets at least 40%
        minSize: 30,
        maxSize: 100,
        reason: `Remaining space (${remainingSpace}px) after optimal allocation`,
      };
    }
  } else {
    // Limited space - use priority-based allocation
    let remainingSpace = usableSpace;

    // First pass: ensure minimum sizes
    areaData.forEach(({ area, minRequired }) => {
      const percentage = Math.round((minRequired / usableSpace) * 100);
      results[area] = {
        defaultSize: percentage,
        minSize: percentage,
        maxSize: percentage * 2,
        reason: `Minimum size (${minRequired}px) due to space constraints`,
      };
      remainingSpace -= minRequired;
    });

    // Second pass: distribute remaining space by priority
    if (remainingSpace > 0) {
      areaData
        .sort((a, b) => b.req.priority - a.req.priority)
        .forEach(({ area, req }) => {
          const priorityWeight = req.priority / totalPriority;
          const extraSpace = remainingSpace * priorityWeight;
          const currentResult = results[area];
          const newSize = currentResult.defaultSize + Math.round((extraSpace / usableSpace) * 100);

          results[area] = {
            ...currentResult,
            defaultSize: newSize,
            maxSize: Math.min(newSize * 1.5, area === LayoutArea.CONTENT ? 100 : 30),
            reason: `Priority-weighted allocation (priority ${req.priority})`,
          };
        });
    }
  }

  // Mobile optimizations
  if (viewportInfo.isMobile) {
    Object.keys(results).forEach((areaKey) => {
      const area = areaKey as LayoutArea;
      const result = results[area];
      const req = CONTENT_REQUIREMENTS[area];

      if (req.type === 'toolbar' || req.type === 'status') {
        // Make toolbars smaller on mobile
        results[area] = {
          ...result,
          defaultSize: Math.max(Math.round(result.defaultSize * 0.8), result.minSize),
          reason: `${result.reason} (mobile optimization)`,
        };
      } else if (area === LayoutArea.CONTENT) {
        // Give content more space on mobile
        results[area] = {
          ...result,
          defaultSize: Math.min(result.defaultSize + 10, 100),
          reason: `${result.reason} (mobile content priority)`,
        };
      }
    });
  }

  return results;
}

/**
 * Calculate sidebar sizes for horizontal layout
 */
export function calculateSidebarSizes(visibleSidebars: LayoutArea[], viewport?: ViewportInfo): Record<LayoutArea, PanelSizeCalculation> {
  return calculateOptimalSizes(visibleSidebars, 'horizontal', viewport);
}

/**
 * Calculate vertical panel sizes
 */
export function calculateVerticalSizes(visibleAreas: LayoutArea[], viewport?: ViewportInfo): Record<LayoutArea, PanelSizeCalculation> {
  return calculateOptimalSizes(visibleAreas, 'vertical', viewport);
}

/**
 * Hook for getting and setting panel sizes using react-resizable-panels native API
 */
export function usePanelSizes(panelGroupId: string, panelIds: LayoutArea[]) {
  const sizesRef = useRef<Record<LayoutArea, number>>({} as Record<LayoutArea, number>);
  const lastLayoutRef = useRef<number[]>([]);

  // Get current panel sizes using react-resizable-panels native methods
  const getCurrentSizes = useCallback((): Record<LayoutArea, number> => {
    const groupElement = getPanelGroupElement(panelGroupId);
    if (!groupElement) {
      return sizesRef.current;
    }

    const currentSizes: Record<LayoutArea, number> = {} as Record<LayoutArea, number>;
    panelIds.forEach((panelId) => {
      const panelElement = getPanelElement(panelId);
      if (panelElement) {
        // Get current size from the DOM element
        const rect = panelElement.getBoundingClientRect();
        const groupRect = groupElement.getBoundingClientRect();

        // Calculate percentage based on the direction of the panel group
        const isHorizontal = groupElement.getAttribute('data-direction') === 'horizontal';
        const percentage = isHorizontal ? Math.round((rect.width / groupRect.width) * 100) : Math.round((rect.height / groupRect.height) * 100);

        currentSizes[panelId] = Math.max(0, Math.min(100, percentage));
      }
    });

    sizesRef.current = currentSizes;
    return currentSizes;
  }, [panelGroupId, panelIds]);

  // Set panel sizes using onLayout callback (the react-resizable-panels way)
  const setPanelSizes = useCallback(
    (newSizes: Record<LayoutArea, number>) => {
      // Convert our sizes object to the array format expected by onLayout
      const sizesArray = panelIds.map((panelId) => newSizes[panelId] || 0);
      lastLayoutRef.current = sizesArray;

      // Note: Actual setting happens via onLayout callback in the PanelGroup
      // This is just for tracking what we want to set
      sizesRef.current = newSizes;
    },
    [panelIds],
  );

  // Handle layout changes from react-resizable-panels
  const handleLayoutChange = useCallback(
    (sizes: number[]) => {
      const newSizes: Record<LayoutArea, number> = {} as Record<LayoutArea, number>;
      panelIds.forEach((panelId, index) => {
        newSizes[panelId] = sizes[index] || 0;
      });

      sizesRef.current = newSizes;
      lastLayoutRef.current = sizes;
    },
    [panelIds],
  );

  return {
    getCurrentSizes,
    setPanelSizes,
    handleLayoutChange,
    currentSizes: sizesRef.current,
    lastLayout: lastLayoutRef.current,
  };
}

/**
 * Hook for intelligent auto-sizing based on content requirements and viewport
 */
export function useAutoSizing(visibleAreas: LayoutArea[], direction: 'horizontal' | 'vertical' = 'vertical') {
  const viewportRef = useRef<ViewportInfo>();
  const calculationsRef = useRef<Record<LayoutArea, PanelSizeCalculation>>({} as Record<LayoutArea, PanelSizeCalculation>);

  // Update viewport info when window resizes
  useEffect(() => {
    const updateViewport = () => {
      viewportRef.current = getViewportInfo();
    };

    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  // Calculate optimal sizes based on current viewport and content requirements
  const calculateOptimalSizesForAreas = useCallback(() => {
    const viewport = viewportRef.current || getViewportInfo();
    const calculations = calculateOptimalSizes(visibleAreas, direction, viewport);
    calculationsRef.current = calculations;

    if (process.env.NODE_ENV === 'development') {
      logSizingDecisions(calculations, `Auto-sizing (${direction})`);
    }

    return calculations;
  }, [visibleAreas, direction]);

  // Get recommended sizes as an array (for react-resizable-panels onLayout)
  const getRecommendedLayout = useCallback((): number[] => {
    const calculations = calculateOptimalSizesForAreas();
    return visibleAreas.map((area) => calculations[area]?.defaultSize || 0);
  }, [calculateOptimalSizesForAreas, visibleAreas]);

  return {
    calculateOptimalSizes: calculateOptimalSizesForAreas,
    getRecommendedLayout,
    currentCalculations: calculationsRef.current,
    viewport: viewportRef.current,
  };
}

/**
 * Hook for responsive panel sizing that adapts to viewport changes
 */
export function useResponsivePanelSizing(panelGroupId: string, visibleAreas: LayoutArea[], direction: 'horizontal' | 'vertical' = 'vertical') {
  const { getCurrentSizes, setPanelSizes, handleLayoutChange } = usePanelSizes(panelGroupId, visibleAreas);
  const { getRecommendedLayout, calculateOptimalSizes } = useAutoSizing(visibleAreas, direction);

  // Auto-adjust sizes when viewport changes significantly
  const adjustForViewport = useCallback(() => {
    const currentSizes = getCurrentSizes();
    const recommendedLayout = getRecommendedLayout();

    // Check if current sizes are significantly different from optimal
    const hasSignificantDifference = visibleAreas.some((area, index) => {
      const current = currentSizes[area] || 0;
      const recommended = recommendedLayout[index] || 0;
      return Math.abs(current - recommended) > 10; // 10% threshold
    });

    if (hasSignificantDifference) {
      const newSizes: Record<LayoutArea, number> = {} as Record<LayoutArea, number>;
      visibleAreas.forEach((area, index) => {
        newSizes[area] = recommendedLayout[index] || 0;
      });
      setPanelSizes(newSizes);
      return recommendedLayout;
    }

    return null;
  }, [getCurrentSizes, getRecommendedLayout, setPanelSizes, visibleAreas]);

  return {
    getCurrentSizes,
    setPanelSizes,
    handleLayoutChange,
    calculateOptimalSizes,
    getRecommendedLayout,
    adjustForViewport,
  };
}

/**
 * Pixel-based sizing utilities
 */

/**
 * Convert a size constraint to pixels based on viewport
 */
export function sizeConstraintToPixels(constraint: number | SizeConstraint, totalPixels: number): number {
  if (typeof constraint === 'number') {
    // Legacy percentage value
    return Math.round((constraint / 100) * totalPixels);
  }

  switch (constraint.unit) {
    case 'pixels':
      return constraint.value;
    case 'percent':
      return Math.round((constraint.value / 100) * totalPixels);
    case 'auto':
      return 0; // Will be calculated based on content
    default:
      return 0;
  }
}

/**
 * Convert pixels to percentage based on viewport
 */
export function pixelsToPercent(pixels: number, totalPixels: number): number {
  return Math.round((pixels / totalPixels) * 100);
}

/**
 * Calculate size constraints from panel config and viewport
 */
export function calculateSizeConstraints(
  area: LayoutArea,
  config: Partial<PanelConfig>,
  viewport: ViewportInfo,
  direction: 'horizontal' | 'vertical' = 'vertical',
): SizeConstraints {
  const totalPixels = direction === 'horizontal' ? viewport.width : viewport.height;
  const contentReq = CONTENT_REQUIREMENTS[area];

  // Start with content requirements as baseline
  const baseMinPixels = direction === 'horizontal' ? contentReq?.minWidth || 0 : contentReq?.minHeight || 0;

  // Calculate pixel constraints
  const minPixels = Math.max(config?.minPixels || baseMinPixels, sizeConstraintToPixels(config?.minSize || 0, totalPixels));

  const maxPixels = Math.min(config?.maxPixels || totalPixels, sizeConstraintToPixels(config?.maxSize || 100, totalPixels));

  // Convert to percentages for react-resizable-panels
  const minPercent = Math.max(0, pixelsToPercent(minPixels, totalPixels));
  const maxPercent = Math.min(100, pixelsToPercent(maxPixels, totalPixels));

  return {
    minPercent,
    maxPercent,
    minPixels,
    maxPixels,
    preferredPixels: direction === 'horizontal' ? contentReq?.optimalWidth : contentReq?.optimalHeight,
    canCollapse: config?.collapsible || false,
    collapseThreshold: config?.collapseThreshold,
  };
}

/**
 * Calculate optimal size with pixel constraints
 */
export function calculateOptimalSizeWithPixelConstraints(
  area: LayoutArea,
  config: Partial<PanelConfig>,
  viewport: ViewportInfo,
  availableSpace: number,
  direction: 'horizontal' | 'vertical' = 'vertical',
): DynamicSizeResult {
  const constraints = calculateSizeConstraints(area, config, viewport, direction);
  const totalPixels = direction === 'horizontal' ? viewport.width : viewport.height;
  const availablePixels = Math.round((availableSpace / 100) * totalPixels);

  let targetPixels = constraints.preferredPixels || constraints.minPixels;
  const adaptations: string[] = [];

  // Auto-sizing based on content requirements
  if (config?.autoSize === 'content' && constraints.preferredPixels) {
    targetPixels = constraints.preferredPixels;
    adaptations.push('content-optimized');
  } else if (config?.autoSize === 'fixed' && config?.minPixels) {
    targetPixels = config.minPixels;
    adaptations.push('fixed-size');
  }

  // Apply constraints
  if (targetPixels < constraints.minPixels) {
    targetPixels = constraints.minPixels;
    adaptations.push('min-constraint-applied');
  }

  if (targetPixels > constraints.maxPixels) {
    targetPixels = constraints.maxPixels;
    adaptations.push('max-constraint-applied');
  }

  // Check if content should collapse
  const shouldCollapse = constraints.canCollapse && constraints.collapseThreshold && availablePixels < constraints.collapseThreshold;

  if (shouldCollapse) {
    targetPixels = config?.collapseSize || Math.min(constraints.minPixels * 0.3, 40);
    adaptations.push('auto-collapsed');
  }

  // Ensure we don't exceed available space
  if (targetPixels > availablePixels) {
    targetPixels = Math.max(availablePixels, constraints.minPixels);
    adaptations.push('space-constrained');
  }

  const finalPercent = pixelsToPercent(targetPixels, totalPixels);

  return {
    size: Math.max(0, Math.min(100, finalPercent)),
    sizePixels: targetPixels,
    constraints,
    reason: `Pixel-aware sizing: ${targetPixels}px (${finalPercent}%)`,
    isCollapsed: Boolean(shouldCollapse),
    adaptations,
  };
}

/**
 * Enhanced hook for pixel-aware responsive panel sizing
 */
export function usePixelAwarePanelSizing(
  panelGroupId: string,
  visibleAreas: LayoutArea[],
  panelConfigs: Record<LayoutArea, Partial<PanelConfig>>,
  direction: 'horizontal' | 'vertical' = 'vertical',
  handlers?: PanelResizeHandlers,
) {
  const { getCurrentSizes, setPanelSizes, handleLayoutChange } = usePanelSizes(panelGroupId, visibleAreas);
  const viewport = useRef<ViewportInfo>(getViewportInfo());
  const constraintsRef = useRef<Record<LayoutArea, SizeConstraints>>({} as Record<LayoutArea, SizeConstraints>);

  // Update viewport info when window resizes
  useEffect(() => {
    const updateViewport = () => {
      viewport.current = getViewportInfo();
      recalculateConstraints();
    };

    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  // Recalculate constraints when config changes
  const recalculateConstraints = useCallback(() => {
    const newConstraints: Record<LayoutArea, SizeConstraints> = {} as Record<LayoutArea, SizeConstraints>;

    visibleAreas.forEach((area) => {
      const config = panelConfigs[area] || {};
      newConstraints[area] = calculateSizeConstraints(area, config, viewport.current, direction);
    });

    constraintsRef.current = newConstraints;
  }, [visibleAreas, panelConfigs, direction]);

  // Calculate recommended layout with pixel constraints
  const getPixelAwareLayout = useCallback((): number[] => {
    const totalSpace = 100; // Total percentage space available
    const results: number[] = [];

    visibleAreas.forEach((area) => {
      const config = panelConfigs[area] || {};
      const availableSpace = totalSpace / visibleAreas.length; // Simplified allocation

      const result = calculateOptimalSizeWithPixelConstraints(area, config, viewport.current, availableSpace, direction);

      results.push(result.size);

      if (process.env.NODE_ENV === 'development') {
        console.log(`🎯 ${area}: ${result.reason}`, {
          size: result.size,
          pixels: result.sizePixels,
          adaptations: result.adaptations,
        });
      }
    });

    return results;
  }, [visibleAreas, panelConfigs, direction]);

  // Enhanced layout change handler with constraint validation
  const handleEnhancedLayoutChange = useCallback(
    (sizes: number[]) => {
      handleLayoutChange(sizes);

      // Validate constraints and trigger handlers
      visibleAreas.forEach((area, index) => {
        const size = sizes[index] || 0;
        const constraints = constraintsRef.current[area];

        if (constraints) {
          const totalPixels = direction === 'horizontal' ? viewport.current.width : viewport.current.height;
          const sizePixels = Math.round((size / 100) * totalPixels);

          // Check if resize violates constraints
          if (sizePixels < constraints.minPixels || sizePixels > constraints.maxPixels) {
            handlers?.onAutoResize?.(area, 'constraint', size);
          }

          // Check for collapse
          const isCollapsed = constraints.canCollapse && constraints.collapseThreshold && sizePixels < constraints.collapseThreshold;

          if (isCollapsed) {
            handlers?.onCollapse?.(area, true);
          }

          handlers?.onResize?.(area, size, constraints);
        }
      });
    },
    [handleLayoutChange, visibleAreas, direction, handlers],
  );

  // Initialize constraints
  useEffect(() => {
    recalculateConstraints();
  }, [recalculateConstraints]);

  return {
    getCurrentSizes,
    setPanelSizes,
    handleLayoutChange: handleEnhancedLayoutChange,
    getPixelAwareLayout,
    getConstraints: () => constraintsRef.current,
    recalculateConstraints,
  };
}

/**
 * Debug helper to log sizing decisions
 */
export function logSizingDecisions(calculations: Record<LayoutArea, PanelSizeCalculation>, context: string): void {
  if (process.env.NODE_ENV === 'development') {
    console.group(`🎯 Dynamic Sizing: ${context}`);
    Object.entries(calculations).forEach(([area, calc]) => {
      console.log(`${area}: ${calc.defaultSize}% (${calc.reason})`);
    });
    console.groupEnd();
  }
}
